// src/pages/markets/board/card/body/ConvictionRow.tsx
//
// What is left of the score block: one figure row under the outcome ladder.
// It describes the *selected* market, so it changes when the reader clicks a
// row above. The ladder answers "where is the money", this answers "how much,
// on which side, and who put it there".
//
// Hovering it opens the trader breakdown for exactly that (market, group).

import { Box } from '@mui/material';
import type { BoardScore, ScreenerEvent } from '../../api/boardTypes';
import { netCapital, positionLabel } from '../../api/netCapital';
import type { CardVariant } from '../../board/columnConfig';
import { CARD_TEST_IDS } from '../cardLayout';
import { formatUsd } from '../cardFormat';
import { HoverPanel } from '../HoverPanel';
import { TraderListPanel } from '../TraderListPanel';
import { TraderDetail } from '../TraderDetail';
import { PanelHead } from '../panelParts';
import {
    CardTail,
    FlowFigures,
    TailNote,
    cardTailStyle,
    cardTailSx,
} from '../CardFigures';
import { traderHref } from '../../board/routes';
import { sideLabels, type SideLabels } from '../../utils/boardOutcomeLabels';
import { OUTCOME_MAXLEN_BADGE } from '../../../utils/outcomeLabels';

// Reserved so the panel is the same rectangle empty, loading and loaded.
const PANEL_MIN_WIDTH = 620;
const PANEL_MIN_HEIGHT = 160;

export interface ConvictionRowProps {
    score: BoardScore | null;
    variant: CardVariant;
    now: number;
    emptyText: string;
    /** The market's true outcome pair, when /api/event-markets has landed. */
    outcomes?: string[] | null;
    event?: Pick<ScreenerEvent, 'markets'> | null;
}

function PanelHeader({
    score,
    labels,
    event,
}: {
    score: BoardScore;
    labels: SideLabels;
    event?: Pick<ScreenerEvent, 'markets'> | null;
}) {
    return (
        <PanelHead
            title={
                score.market_question ??
                score.outcome_yes_label ??
                score.market_id
            }
            meta={
                <>
                    Net {formatUsd(Math.abs(netCapital(score)))}{' '}
                    {positionLabel(event, score, labels)} · {labels.yes.text}{' '}
                    {formatUsd(score.capital_usd_yes)} · {labels.no.text}{' '}
                    {formatUsd(score.capital_usd_no)}
                </>
            }
        />
    );
}

export function ConvictionRow({
    score,
    now,
    emptyText,
    outcomes,
    event,
}: ConvictionRowProps) {
    const labels = sideLabels(score, OUTCOME_MAXLEN_BADGE, { outcomes });

    if (!score) {
        return (
            <CardTail columns={1} testId={CARD_TEST_IDS.conviction}>
                <TailNote>{emptyText}</TailNote>
            </CardTail>
        );
    }

    return (
        <HoverPanel
            placement="right-start"
            padded={false}
            minWidth={PANEL_MIN_WIDTH}
            minHeight={PANEL_MIN_HEIGHT}
            testId={CARD_TEST_IDS.conviction}
            content={() => (
                <Box sx={{ minWidth: PANEL_MIN_WIDTH }}>
                    <PanelHeader score={score} labels={labels} event={event} />
                    <TraderListPanel
                        marketId={score.market_id}
                        group={score.group}
                        score={score}
                        outcomes={outcomes}
                        now={now}
                        traderHref={traderHref}
                        rowDetail={t => <TraderDetail address={t.address} />}
                        detailPlacement="right"
                    />
                </Box>
            )}
            style={cardTailStyle}
            sx={{
                ...cardTailSx(3),
                borderRadius: '0 0 3px 3px',
                transition: 'background-color 120ms',
                cursor: 'default',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            <FlowFigures score={score} labels={labels} event={event} />
        </HoverPanel>
    );
}
